// FIG Third-party: what outside services say about the same sample, one row per provider the backend has enabled
// (thirdParty.ts, GET /v1/samples/{sha256}/intel, in the backend's order): service · type · mode (lookup, or lookup +
// submit: the backend uploads the sample when the service has not seen it, then polls) · status · result (their verdict,
// their score — VirusTotal's is the share of engines that flagged it — the family, tags: YARAify's are the rules that
// matched; while analyzing, malbox's submission id) · first seen · last seen · checked · report. Refresh asks every
// service again (?force=1). No intel on the backend: one line says so.
//   meta(ctx) → { id: "thirdparty", label: "Third-party", empty: no sample hash (URL runs) }
//   <ThirdPartySection job no hideOff? className? />   the same section for the live cockpit and the queued view: the
//       lookups start as soon as the run exists, every service reads "checking" until its answer lands; hideOff renders
//       nothing while the backend has no intel (or the run has no sample hash); className replaces "cell fig"
//   useThirdPartyRow(job) → KvRow | null   the lenses' "Third-party" row ("2 malicious · 1 analyzing · of 5"); null
//                                          while the backend has no intel or the run has no sample hash
import { Alert, Button, Empty, FigHead, LiveBadge, Loading, MicroTag, Time, VerdictBanner, figTitleId, type KvRow } from "@/components/ui";
import { cx } from "@/components/ui/cx";
import { count } from "@/lib/format";
import type { FigMeta, FigProps, ReportCtx } from "@/lib/derive/types";
import type { Job } from "@/types";
import { providerInfo, tally, useThirdParty, type ThirdPartyResult } from "../thirdParty";

export function meta(ctx: ReportCtx): FigMeta {
  return { id: "thirdparty", label: "Third-party", empty: !ctx.job.sample_sha };
}

export function useThirdPartyRow(job: Pick<Job, "sample_sha">): KvRow | null {
  const tp = useThirdParty(job);
  if (tp.mode === "off") return null;
  if (tp.mode === "config" || tp.mode === "loading") return ["Third-party", <span className="text-ink-faint">checking…</span>];
  if (tp.mode === "error") return ["Third-party", <span className="text-ink-faint">lookup failed</span>];
  const t = tally(tp.rows);
  const parts: string[] = [];
  if (t.malicious) parts.push(`${count(t.malicious)} malicious`);
  if (t.suspicious) parts.push(`${count(t.suspicious)} suspicious`);
  if (!t.malicious && !t.suspicious && t.benign) parts.push(`${count(t.benign)} benign`);
  if (t.analyzing) parts.push(`${count(t.analyzing)} analyzing`);
  if (!parts.length) parts.push(t.found ? `${count(t.found)} found` : "not found");
  const [head, ...rest] = parts;
  return [
    "Third-party",
    <>
      {head}
      <small>
        {rest.map((p) => ` · ${p}`).join("")} · of {count(t.total)}
      </small>
    </>,
  ];
}

function Status({ r }: { r: ThirdPartyResult }) {
  switch (r.status) {
    case "checking":
      return <LiveBadge size="sm" label="checking" />;
    case "analyzing":
      return <LiveBadge size="sm" label="analyzing" title="Submitted by malbox; the result appears when this service finishes its analysis" />;
    case "ok":
      return <span className="text-ink">found</span>;
    case "not_found":
      return <span className="text-ink-faint">not found</span>;
    case "disabled":
      return <span className="text-ink-faint">disabled</span>;
    case "error":
      return (
        <span className="text-ink" title={r.error}>
          <span aria-hidden="true">× </span>error
        </span>
      );
    default:
      return <span className="text-ink-faint">{String(r.status || "unknown")}</span>;
  }
}

function Result({ r }: { r: ThirdPartyResult }) {
  if (r.status === "analyzing")
    return (
      <span className="text-ink-faint">
        submitted
        {r.submission_id && (
          <>
            {" · "}
            <span className="id" translate="no" title={r.submission_id}>
              {r.submission_id.length > 24 ? `${r.submission_id.slice(0, 12)}…${r.submission_id.slice(-8)}` : r.submission_id}
            </span>
          </>
        )}
      </span>
    );
  if (r.status !== "ok") return <span className="text-ink-faint">{r.status === "error" && r.error ? <span title={r.error}>{r.error.slice(0, 48)}</span> : "—"}</span>;
  const info = providerInfo(r.provider);
  const tags = [...new Set((r.tags ?? []).filter(Boolean))];
  const shown = tags.slice(0, info.tags ?? 3);
  const engines = info.engines;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      {r.verdict && <VerdictBanner v={r.verdict} size="sm" />}
      {r.score !== undefined && r.score !== null && (
        <span title={engines ? `${r.score}% of engines flagged it` : `score ${r.score} of 100`}>
          <b className="font-medium text-ink">{r.score}</b>
          <span className="text-ink-faint">{engines ? "% engines" : "/100"}</span>
        </span>
      )}
      {r.family && (
        <span className="primary" translate="no">
          {r.family}
        </span>
      )}
      {shown.map((t) => (
        <MicroTag key={t}>{t}</MicroTag>
      ))}
      {tags.length > shown.length && <span className="text-ink-faint" title={tags.slice(shown.length).join(", ")}>+{tags.length - shown.length}</span>}
    </span>
  );
}

/** A provider's sightings span years: a full date. The lookup time: month, day and time. */
const real = (at?: string): at is string => !!at && !at.startsWith("0001-") && Number.isFinite(Date.parse(at));
const Day = ({ at }: { at?: string }) => (real(at) ? <Time value={at}>{new Date(at).toISOString().slice(0, 10)}</Time> : <>—</>);
const When = ({ at }: { at?: string }) => (real(at) ? <Time value={at} mode="date" /> : <>—</>);

export default function ThirdPartyFig({ ctx, no }: FigProps) {
  return <ThirdPartySection job={ctx.job} no={no} />;
}

export function ThirdPartySection({ job, no, hideOff, className }: { job: Pick<Job, "sample_sha">; no: number; hideOff?: boolean; className?: string }) {
  const tp = useThirdParty(job);
  if (hideOff && tp.mode === "off") return null;
  const found = tp.rows.filter((r) => r.status === "ok").length;
  const analyzing = tp.rows.filter((r) => r.status === "analyzing").length;
  const countText =
    tp.mode === "off"
      ? "not configured"
      : tp.mode === "ready"
        ? `${count(found)} of ${count(tp.rows.length)} found${analyzing ? ` · ${count(analyzing)} analyzing` : ""}`
        : tp.mode === "error"
          ? "lookup failed"
          : "checking…";
  const canRefresh = tp.mode === "ready" || tp.mode === "error";

  let body;
  if (tp.mode === "off") body = <Empty inline>No third-party services configured</Empty>;
  else if (tp.mode === "config") body = <Loading>Reading /v1/config…</Loading>;
  else if (tp.mode === "error") body = <Alert head="Couldn't load third-party results">{tp.error}</Alert>;
  else
    body = (
      <div className="tscroll relative">
        <table className="t">
          <thead>
            <tr>
              <th>service</th>
              <th>type</th>
              <th>mode</th>
              <th>status</th>
              <th>result</th>
              <th className="r">first seen</th>
              <th className="r">last seen</th>
              <th className="r">checked</th>
              <th className="r">report</th>
            </tr>
          </thead>
          <tbody>
            {tp.rows.map((r) => {
              const info = providerInfo(r.provider);
              return (
                <tr key={r.provider}>
                  <td className="primary">{info.name}</td>
                  <td>{info.kind}</td>
                  <td title={info.submits ? "Looked up by hash; uploaded by malbox when this service has not seen it" : "Looked up by hash only"}>
                    {info.submits ? "lookup + submit" : "lookup"}
                  </td>
                  <td>
                    <Status r={r} />
                  </td>
                  <td>
                    <Result r={r} />
                  </td>
                  <td className="r" data-none={real(r.first_seen) ? undefined : ""}>
                    <Day at={r.first_seen} />
                  </td>
                  <td className="r" data-none={real(r.last_seen) ? undefined : ""}>
                    <Day at={r.last_seen} />
                  </td>
                  <td className="r" data-none={real(r.fetched_at) ? undefined : ""}>
                    <When at={r.fetched_at} />
                  </td>
                  <td className="r" data-none={r.web_url ? undefined : ""}>
                    {r.web_url ? (
                      <a className="lnk" href={r.web_url} target="_blank" rel="noopener noreferrer">
                        open ↗
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );

  return (
    <section className={cx(className ?? "cell fig")} aria-labelledby={figTitleId(no)}>
      <FigHead
        no={no}
        label="Third-party analysis"
        count={countText}
        actions={
          canRefresh ? (
            <Button size="sm" icon="refresh" disabled={tp.refreshing} onClick={tp.refresh} title="Ask every service again, skipping the backend's cache">
              {tp.refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          ) : undefined
        }
      />
      <div className="fig-body">
        {body}
        {tp.refreshError && (
          <Alert head="Couldn't refresh" className="mt-4">
            {tp.refreshError}
          </Alert>
        )}
      </div>
    </section>
  );
}
